(() => {
  const SCRIPT_VERSION = '3.3.0';

  const state = {
    cart: null,
    toastTimer: null,
    refreshTimer: null,
    translations: null,
  };

  const routesFallback = {
    add: '/cart/add.js',
    cart: '/cart.js',
    cartPage: '/cart',
  };

  const fallbackTranslations = {
    tiers: {
      zero: {
        title: 'Combine locations and save up to 20%',
        subtitle: 'The discount applies to different individual locations in the same cart.',
        active: 'No volume discount active yet',
      },
      one: {
        title: 'Add 1 more location for 5% off',
        subtitle: 'Add another individual location to reach the first discount tier.',
        active: '1 individual location in your cart',
      },
      two: {
        title: '5% off active · add 1 more for 10%',
        subtitle: 'You have reached the first discount tier.',
        active: '5% volume discount active',
      },
      three: {
        title: '10% off active · add 1 more for 15%',
        subtitle: 'Add one more different location to reach the next tier.',
        active: '10% volume discount active',
      },
      four: {
        title: '15% off active · add 1 more for 20%',
        subtitle: 'You are one location away from the highest tier.',
        active: '15% volume discount active',
      },
      maximum: {
        title: '20% volume discount active',
        subtitle: '__COUNT__ different individual locations in your cart.',
        active: 'Highest volume discount reached',
      },
    },
    buttons: {
      add: 'Add',
      added: 'In cart ✓',
    },
    modal: {
      eyebrow: 'Adding more pays off',
      title: 'Added to your cart',
      close: 'Close',
      twoLocations: '2 locations',
      threeLocations: '3 locations',
      fourLocations: '4 locations',
      fiveOrMore: '5 or more',
      discount5: '5% off',
      discount10: '10% off',
      discount15: '15% off',
      discount20: '20% off',
      continue: 'Continue shopping',
      viewCart: 'View cart',
    },
    toast: {
      added: 'Location added',
      error: 'Could not add',
    },
    errors: {
      cartLoad: 'The cart could not be loaded.',
      addLocation: 'The location could not be added.',
    },
  };

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  function readTranslations() {
    const node = document.querySelector('[data-un-commerce-translations]');

    if (!node) return fallbackTranslations;

    try {
      return {
        ...fallbackTranslations,
        ...JSON.parse(node.textContent),
      };
    } catch (error) {
      console.warn('[UrbexNow Commerce V3] Invalid translation data.', error);
      return fallbackTranslations;
    }
  }

  function translations() {
    if (!state.translations) {
      state.translations = readTranslations();
    }
    return state.translations;
  }

  function interpolate(value, variables = {}) {
    return Object.entries(variables).reduce(
      (result, [key, replacement]) =>
        String(result).replaceAll(`__${key.toUpperCase()}__`, String(replacement)),
      String(value || '')
    );
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function isLocationItem(item) {
    const privateKind = normalize(
      item.properties &&
        (item.properties._urbex_product_kind ||
          item.properties['Urbex producttype'])
    );

    const productType = normalize(item.product_type);
    const handle = normalize(item.handle);
    const title = normalize(item.product_title || item.title);

    return (
      privateKind === 'location' ||
      privateKind === 'locatie' ||
      productType.includes('losse locatie') ||
      handle.includes('losse-locatie') ||
      /#[0-9]{3,}/.test(title)
    );
  }

  function getUniqueLocationCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    const ids = new Set();

    cart.items.forEach((item) => {
      if (isLocationItem(item)) {
        ids.add(String(item.product_id || item.handle || item.key));
      }
    });

    return ids.size;
  }

  function getTierState(count) {
    const tierStrings = translations().tiers || fallbackTranslations.tiers;

    if (count <= 0) {
      return { percent: 0, ...tierStrings.zero };
    }

    if (count === 1) {
      return { percent: 20, ...tierStrings.one };
    }

    if (count === 2) {
      return { percent: 40, ...tierStrings.two };
    }

    if (count === 3) {
      return { percent: 60, ...tierStrings.three };
    }

    if (count === 4) {
      return { percent: 80, ...tierStrings.four };
    }

    return {
      percent: 100,
      ...tierStrings.maximum,
      subtitle: interpolate(tierStrings.maximum.subtitle, { count }),
    };
  }

  async function fetchCart() {
    const cartUrl =
      window.routes && window.routes.cart_url
        ? `${window.routes.cart_url}.js`
        : routesFallback.cart;

    const response = await fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(translations().errors.cartLoad);
    }

    state.cart = await response.json();
    return state.cart;
  }

  function updateHeaderCartCount(cart) {
    const itemCount = cart && Number(cart.item_count || 0);

    document
      .querySelectorAll(
        '.cart-count-bubble span[aria-hidden="true"], .cart-count-bubble span:first-child'
      )
      .forEach((element) => {
        element.textContent = String(itemCount);
      });

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(itemCount);
    });
  }

  function updateTierBars(cart) {
    const count = getUniqueLocationCount(cart);
    const tier = getTierState(count);

    document.querySelectorAll('[data-un-tier-bar]').forEach((bar) => {
      const title = bar.querySelector('[data-un-tier-title]');
      const subtitle = bar.querySelector('[data-un-tier-subtitle]');
      const progress = bar.querySelector('[data-un-tier-progress]');
      const counter = bar.querySelector('[data-un-location-count]');

      if (title) title.textContent = tier.title;
      if (subtitle) subtitle.textContent = tier.subtitle;
      if (progress) progress.style.width = `${tier.percent}%`;
      if (counter) counter.textContent = String(count);
    });
  }

  function updateButtons(cart) {
    const productIds = new Set();

    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        if (isLocationItem(item)) {
          productIds.add(String(item.product_id));
        }
      });
    }

    const buttonStrings = translations().buttons || fallbackTranslations.buttons;

    document.querySelectorAll('[data-un-location-add]').forEach((button) => {
      const productId = String(button.dataset.unProductId || '');
      const label = button.querySelector('[data-un-add-label]');
      const added = productIds.has(productId);

      button.classList.toggle('is-added', added);

      if (label) {
        label.textContent = added ? buttonStrings.added : buttonStrings.add;
      }
    });
  }

  function ensureDialog() {
    let dialog = document.querySelector('[data-un-tier-modal]');

    if (dialog) return dialog;

    const modal = translations().modal || fallbackTranslations.modal;
    const cartUrl =
      (window.routes && window.routes.cart_url) || routesFallback.cartPage;

    dialog = document.createElement('dialog');
    dialog.className = 'un-v3-modal';
    dialog.setAttribute('data-un-tier-modal', '');
    dialog.innerHTML = `
      <div class="un-v3-modal__inner">
        <div class="un-v3-modal__top">
          <div>
            <span>${escapeHtml(modal.eyebrow)}</span>
            <h2>${escapeHtml(modal.title)}</h2>
          </div>

          <button
            type="button"
            class="un-v3-modal__close"
            data-un-modal-close
            aria-label="${escapeHtml(modal.close)}"
          >×</button>
        </div>

        <div class="un-v3-modal__status">
          <strong data-un-modal-status></strong>
          <span data-un-modal-next></span>
        </div>

        <div class="un-v3-modal__tiers">
          <div class="un-v3-modal__tier"><span>${escapeHtml(modal.twoLocations)}</span><b>${escapeHtml(modal.discount5)}</b></div>
          <div class="un-v3-modal__tier"><span>${escapeHtml(modal.threeLocations)}</span><b>${escapeHtml(modal.discount10)}</b></div>
          <div class="un-v3-modal__tier"><span>${escapeHtml(modal.fourLocations)}</span><b>${escapeHtml(modal.discount15)}</b></div>
          <div class="un-v3-modal__tier"><span>${escapeHtml(modal.fiveOrMore)}</span><b>${escapeHtml(modal.discount20)}</b></div>
        </div>

        <div class="un-v3-modal__actions">
          <button type="button" data-un-modal-close>${escapeHtml(modal.continue)}</button>
          <a href="${escapeHtml(cartUrl)}">${escapeHtml(modal.viewCart)}</a>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);

    dialog.addEventListener('click', (event) => {
      if (
        event.target === dialog ||
        event.target.closest('[data-un-modal-close]')
      ) {
        dialog.close();
      }
    });

    return dialog;
  }

  function showTierModal(cart) {
    const count = getUniqueLocationCount(cart);
    const tier = getTierState(count);
    const dialog = ensureDialog();

    const status = dialog.querySelector('[data-un-modal-status]');
    const next = dialog.querySelector('[data-un-modal-next]');

    if (status) status.textContent = tier.active;
    if (next) next.textContent = tier.title;

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  function ensureToast() {
    let toast = document.querySelector('[data-un-tier-toast]');

    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'un-v3-toast';
    toast.setAttribute('data-un-tier-toast', '');
    toast.setAttribute('role', 'status');
    toast.innerHTML = `
      <strong data-un-toast-title></strong>
      <span data-un-toast-text></span>
    `;

    document.body.appendChild(toast);
    return toast;
  }

  function showToast(cart) {
    const count = getUniqueLocationCount(cart);
    const tier = getTierState(count);
    const toast = ensureToast();

    const title = toast.querySelector('[data-un-toast-title]');
    const text = toast.querySelector('[data-un-toast-text]');

    if (title) title.textContent = translations().toast.added;
    if (text) text.textContent = tier.title;

    window.clearTimeout(state.toastTimer);
    toast.classList.add('is-visible');

    state.toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 3600);
  }

  async function addLocation(button) {
    if (button.classList.contains('is-loading')) return;

    const variantId = Number(button.dataset.unVariantId);

    if (!variantId) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');

    try {
      const addUrl =
        (window.routes && window.routes.cart_add_url) || routesFallback.add;

      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _urbex_product_kind: 'location',
              },
            },
          ],
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(
          result.description ||
            result.message ||
            translations().errors.addLocation
        );
      }

      const cart = await fetchCart();

      updateHeaderCartCount(cart);
      updateTierBars(cart);
      updateButtons(cart);

      document.dispatchEvent(
        new CustomEvent('urbexnow:location-added', {
          detail: {
            cart,
            productId: button.dataset.unProductId,
            variantId,
          },
        })
      );

      const modalSeen = sessionStorage.getItem('urbexnow-tier-modal-seen');

      if (!modalSeen) {
        sessionStorage.setItem('urbexnow-tier-modal-seen', '1');
        showTierModal(cart);
      } else {
        showToast(cart);
      }
    } catch (error) {
      const toast = ensureToast();
      const title = toast.querySelector('[data-un-toast-title]');
      const text = toast.querySelector('[data-un-toast-text]');

      if (title) title.textContent = translations().toast.error;
      if (text) text.textContent = error.message;

      toast.classList.add('is-visible');
      window.clearTimeout(state.toastTimer);
      state.toastTimer = window.setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 4500);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  }

  function removeAvailabilityFacets() {
    document
      .querySelectorAll(
        '.un-v3-facets .js-filter, .un-v3-facets .mobile-facets__details'
      )
      .forEach((facet) => {
        if (facet.querySelector('[name="filter.v.availability"]')) {
          facet.remove();
        }
      });
  }

  function bindDelegatedEvents() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-un-location-add]');

      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      addLocation(button);
    });
  }

  let refreshInProgress = false;

  async function refresh() {
    if (refreshInProgress) return;

    refreshInProgress = true;
    state.translations = readTranslations();
    removeAvailabilityFacets();

    try {
      const cart = await fetchCart();
      updateHeaderCartCount(cart);
      updateTierBars(cart);
      updateButtons(cart);
    } catch (error) {
      console.warn('[UrbexNow Commerce V3]', error);
    } finally {
      refreshInProgress = false;
    }
  }

  function scheduleRefresh(delay = 850) {
    window.clearTimeout(state.refreshTimer);

    state.refreshTimer = window.setTimeout(() => {
      refresh();
    }, delay);
  }

  function bindFacetRefresh() {
    document.addEventListener('change', (event) => {
      if (
        event.target.closest('.facets-container') ||
        event.target.closest('facet-filters-form') ||
        event.target.closest('.mobile-facets')
      ) {
        scheduleRefresh();
      }
    });

    document.addEventListener('click', (event) => {
      if (
        event.target.closest('.active-facets') ||
        event.target.closest('.facets__reset') ||
        event.target.closest('.mobile-facets__clear')
      ) {
        scheduleRefresh();
      }
    });

    window.addEventListener('popstate', () => {
      scheduleRefresh(700);
    });
  }

  if (
    window.UrbexNowCommerceV3 &&
    window.UrbexNowCommerceV3.version === SCRIPT_VERSION
  ) {
    window.UrbexNowCommerceV3.refresh();
    return;
  }

  bindDelegatedEvents();
  bindFacetRefresh();

  document.addEventListener('DOMContentLoaded', refresh);
  document.addEventListener('shopify:section:load', refresh);
  window.setTimeout(refresh, 0);

  window.UrbexNowCommerceV3 = {
    version: SCRIPT_VERSION,
    refresh,
    getUniqueLocationCount,
    getTierState,
  };
})();
